import { WebAuthnError } from '@simplewebauthn/browser';
import { getLoginFlowError } from './getApiErrorString';

// The browser ceremony throws a `WebAuthnError` whose `code` pins down the cause — which decides
// whether retrying can help. Cancellation/timeout is retryable; an unsupported device, an insecure
// origin / misconfigured relying party, or an already-registered credential are not, so those steer
// the user elsewhere instead of telling them to "try again". A rejected `verify` is an Axios error
// and flows through `getLoginFlowError` so 429s and server messages still come through (retryable).
const TERMINAL_WEBAUTHN_CODES = new Set([
  'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED',
  'ERROR_INVALID_DOMAIN',
  'ERROR_INVALID_RP_ID',
  'ERROR_MALFORMED_PUBKEYCREDPARAMS',
  'ERROR_INVALID_USER_ID_LENGTH',
  'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT',
  'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT',
  'ERROR_AUTHENTICATOR_NO_SUPPORTED_PUBKEYCREDPARAMS_ALG',
  'ERROR_AUTHENTICATOR_GENERAL_ERROR',
]);

/** Maps a passkey-ceremony failure to a user-facing message. Shared by login and Security management. */
export const passkeyErrorMessage = (error: unknown): string => {
  if (!(error instanceof WebAuthnError)) {
    return getLoginFlowError(error);
  }

  switch (error.code) {
    case 'ERROR_CEREMONY_ABORTED':
      return 'The passkey prompt was cancelled or timed out. Try again, or choose a different method.';
    case 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED':
      return 'This device already has a passkey for your account. Use it to sign in, or choose a different method.';
    case 'ERROR_INVALID_DOMAIN':
    case 'ERROR_INVALID_RP_ID':
    case 'ERROR_MALFORMED_PUBKEYCREDPARAMS':
    case 'ERROR_INVALID_USER_ID_LENGTH':
      return "Passkeys aren't available on this site right now. Please choose a different method.";
    case 'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT':
    case 'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT':
    case 'ERROR_AUTHENTICATOR_NO_SUPPORTED_PUBKEYCREDPARAMS_ALG':
    case 'ERROR_AUTHENTICATOR_GENERAL_ERROR':
      return "This device can't complete a passkey sign-in. Please choose a different method.";
    default:
      return 'Something went wrong with the passkey. Try again, or choose a different method.';
  }
};

/**
 * Whether retrying the ceremony could plausibly succeed. Cancellation/timeout and non-WebAuthn
 * failures (network, 429, transient server errors) are retryable; an unsupported device, insecure
 * origin / misconfigured relying party, or an already-registered credential are not — so the UI can
 * suppress an unhelpful "try again" for those.
 */
export const isRetryablePasskeyError = (error: unknown): boolean => {
  if (!(error instanceof WebAuthnError)) {
    return true;
  }

  return !TERMINAL_WEBAUTHN_CODES.has(error.code);
};
